/**
 * Cloud recording — server-side, composited by the mixer MCU.
 *
 * Replaces the `mode: 'cloud'` branch of `useMeetingRecording`, which was never
 * a cloud recording at all: it called `getDisplayMedia` (prompting the user to
 * pick a window), recorded locally with MediaRecorder, and on stop logged
 * "Cloud upload simulation - would upload to cloud storage".
 *
 * Nothing here touches media. The mixer already composites the room for the
 * SFU/Mix view, so recording is a control call — no second encode on the
 * participant's machine, no screen-share prompt, and the recording keeps
 * running if this tab closes.
 *
 * `useLocalRecording` remains the right tool when the user wants a file on
 * their own disk, or when there is no meeting id to record against.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getRecordingStatus,
  startRecording,
  stopRecording,
  type Recording,
} from '../services/recordings.api'

export type CloudRecordingState = {
  isRecording: boolean
  /** True from the click until the server confirms, so the button can't be spammed. */
  isPending: boolean
  startedAt: number | null
  duration: number
  formattedDuration: string
  error: string | null
  recording: Recording | null
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return hrs > 0
    ? `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

export function useCloudRecording(
  meetingId: string | null,
  onStateChange?: (recording: boolean) => void,
) {
  const [isRecording, setIsRecording] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [recording, setRecording] = useState<Recording | null>(null)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const notify = useRef(onStateChange)
  notify.current = onStateChange
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // A second click before React re-renders sees the same isPending/isRecording
  // state as the first. This ref updates synchronously, so it catches what the
  // state guards below miss.
  const inFlightRef = useRef(false)

  // Read inside applyStatus without pulling isRecording into its deps. This
  // stays stable across renders so refresh() and the mount effect can share it.
  const isRecordingRef = useRef(isRecording)
  isRecordingRef.current = isRecording

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const applyActive = useCallback((row: Recording | null, since: number | null) => {
    if (!mountedRef.current) return
    setRecording(row)
    setIsRecording(!!row)
    setStartedAt(since)
    notify.current?.(!!row)
  }, [])

  // Shared by the mount probe and refresh(): apply whatever the server says is
  // active right now. Only clears local state when we thought we were
  // recording. An inactive status on first mount should not fight the
  // "start" button that has not been pressed yet.
  const applyStatus = useCallback(
    (status: { active: boolean } & Partial<Recording>) => {
      // The server still reports a stopped recording as active while its file
      // uploads. Taken at face value, everyone but the person who pressed Stop
      // kept the REC indicator until the upload finished.
      if (status.active && status.status !== 'uploading') {
        const since = status.startedAt ? new Date(status.startedAt).getTime() : Date.now()
        applyActive(status as Recording, since)
      } else if (isRecordingRef.current) {
        applyActive(null, null)
      }
    },
    [applyActive],
  )

  /**
   * Adopt a recording that is already running.
   *
   * The recording lives on the server, so it survives a reload, a reconnect, or
   * being started by the host on another device. Without this the button would
   * read "start" for a meeting that is already being recorded, and clicking it
   * would look like a no-op.
   */
  useEffect(() => {
    if (!meetingId) return
    let cancelled = false

    getRecordingStatus(meetingId)
      .then((status) => {
        if (cancelled || !mountedRef.current) return
        applyStatus(status)
      })
      .catch(() => {
        // A failed status probe is not worth an error banner — the user has not
        // asked for anything yet. The button simply reads "start".
      })

    return () => {
      cancelled = true
    }
  }, [meetingId, applyStatus])

  /**
   * Re-check the server's recording status on demand.
   *
   * Used when this client learns (over the room channel) that someone else
   * started or stopped the cloud recording, and by the periodic poll that
   * covers a participant who cannot see that channel event at all.
   */
  const refresh = useCallback(async () => {
    if (!meetingId) return
    try {
      const status = await getRecordingStatus(meetingId)
      if (!mountedRef.current) return
      applyStatus(status)
    } catch {
      // Keep whatever we last knew rather than flipping state on a network blip.
    }
  }, [meetingId, applyStatus])

  // Tick from startedAt rather than counting up, so the elapsed time stays
  // right across a reload of a recording that began ten minutes ago.
  useEffect(() => {
    clearTimer()
    if (!isRecording || startedAt == null) {
      setDuration(0)
      return
    }
    setDuration(Math.floor((Date.now() - startedAt) / 1000))
    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startedAt) / 1000))
    }, 1000)
    return clearTimer
  }, [isRecording, startedAt, clearTimer])

  const start = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return false
    if (!meetingId) {
      setError('This meeting cannot be recorded to the cloud')
      return false
    }
    if (isRecording || isPending) return false

    inFlightRef.current = true
    setIsPending(true)
    setError(null)
    try {
      const row = await startRecording(meetingId)
      const since = row.startedAt ? new Date(row.startedAt).getTime() : Date.now()
      applyActive(row, since)
      return true
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Could not start recording')
      return false
    } finally {
      inFlightRef.current = false
      if (mountedRef.current) setIsPending(false)
    }
  }, [meetingId, isRecording, isPending, applyActive])

  const stop = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return false
    if (!meetingId || !isRecording || isPending) return false

    inFlightRef.current = true
    setIsPending(true)
    try {
      await stopRecording(meetingId)
      applyActive(null, null)
      return true
    } catch (err) {
      // The state is left as recording on purpose: if the stop did not land,
      // the mixer is still writing, and showing "stopped" would tell the user
      // something untrue about a meeting that is still being captured.
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Could not stop recording')
      return false
    } finally {
      inFlightRef.current = false
      if (mountedRef.current) setIsPending(false)
    }
  }, [meetingId, isRecording, isPending, applyActive])

  useEffect(() => clearTimer, [clearTimer])

  return {
    isRecording,
    isPending,
    startedAt,
    duration,
    formattedDuration: formatDuration(duration),
    error,
    recording,
    /** Cloud recording needs a meeting to attach to; ad-hoc rooms cannot use it. */
    isAvailable: !!meetingId,
    start,
    stop,
    refresh,
    clearError: () => setError(null),
  }
}
